import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Menu, X } from "lucide-react";
import AnnouncementBar from "./AnnouncementBar";
import { NAV, EVENT } from "@/data/site";
import { CTAButton } from "@/components/primitives";
import LeadDialog, { DOCS } from "@/components/forms/LeadDialog";
import { cn } from "@/lib/utils";

function NavDownload({ action, label, className, testId }) {
  const isBrochure = action === "brochure";
  const doc = isBrochure ? DOCS.brochure : DOCS.sponsorProposal;
  return (
    <LeadDialog
      formType={isBrochure ? "brochure" : "sponsorship_proposal"}
      title={isBrochure ? "Download the Forum Brochure" : "Download the Sponsorship Proposal"}
      description={isBrochure ? "Programme, expo, sponsorship & rates — instant PDF download." : "Tiers, deliverables, stall options and rates — instant PDF download."}
      ctaLabel={isBrochure ? "Get the brochure" : "Get the proposal"}
      successMsg="Sent. Your download is starting now."
      downloadUrl={doc.url}
      downloadName={doc.name}
      trigger={<button type="button" data-testid={testId} className={className}>{label}</button>}
    />
  );
}

function DesktopItem({ item }) {
  const [open, setOpen] = useState(false);
  if (!item.children) {
    return (
      <Link
        data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
        to={item.to}
        className="px-3 py-2 text-sm font-medium text-navy/80 transition-colors hover:text-emeraldDeep"
      >
        {item.label}
      </Link>
    );
  }
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <Link
        data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
        to={item.to}
        className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-navy/80 transition-colors hover:text-emeraldDeep"
      >
        {item.label}
        <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-180")} />
      </Link>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.18 }}
            className="absolute left-0 top-full w-60 pt-3"
          >
            <div className="overflow-hidden rounded-2xl border border-border bg-white p-2 shadow-xl">
              {item.children.map((c) => (
                c.action ? (
                  <NavDownload
                    key={c.label}
                    action={c.action}
                    label={c.label}
                    testId={`nav-sub-${c.label.toLowerCase().replace(/\s/g, "-")}`}
                    className="block w-full rounded-xl px-4 py-2.5 text-left text-sm font-medium text-emeraldDeep transition-colors hover:bg-parchment"
                  />
                ) : (
                  <Link
                    key={c.to}
                    data-testid={`nav-sub-${c.label.toLowerCase().replace(/\s/g, "-")}`}
                    to={c.to}
                    className="block rounded-xl px-4 py-2.5 text-sm font-medium text-navy/80 transition-colors hover:bg-parchment hover:text-emeraldDeep"
                  >
                    {c.label}
                  </Link>
                )
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      <AnnouncementBar />
      <div className={cn(
        "glass border-b transition-all duration-300",
        scrolled ? "border-border bg-parchment/85" : "border-transparent bg-parchment/60"
      )}>
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3 md:px-12">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
            <img src={EVENT.logo} alt="Bharat Ethanol Forum" className="h-12 w-12 object-contain" />
            <span className="hidden flex-col leading-none sm:flex">
              <span className="font-display text-base font-semibold tracking-tight text-navy">Bharat Ethanol Forum</span>
              <span className="text-[11px] font-medium uppercase tracking-[0.18em] text-emeraldDeep">{EVENT.datesShort} · {EVENT.city}</span>
            </span>
          </Link>

          <nav className="hidden items-center lg:flex">
            {NAV.map((item) => <DesktopItem key={item.label} item={item} />)}
          </nav>

          <div className="flex items-center gap-3">
            <CTAButton to="/register" data-testid="header-register-btn" className="hidden px-5 py-2.5 sm:inline-flex">
              Register Now
            </CTAButton>
            <button
              data-testid="mobile-menu-toggle"
              className="lg:hidden text-navy"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="Menu"
            >
              {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden overflow-hidden border-b border-border bg-parchment"
          >
            <div className="space-y-1 px-6 py-4">
              {NAV.map((item) => (
                <div key={item.label}>
                  <Link to={item.to} className="block py-2 font-semibold text-navy">{item.label}</Link>
                  {item.children && (
                    <div className="ml-3 border-l border-border pl-3">
                      {item.children.map((c) => (
                        c.action ? (
                          <NavDownload
                            key={c.label}
                            action={c.action}
                            label={c.label}
                            testId={`nav-mobile-sub-${c.label.toLowerCase().replace(/\s/g, "-")}`}
                            className="block w-full py-1.5 text-left text-sm font-medium text-emeraldDeep"
                          />
                        ) : (
                          <Link key={c.to} to={c.to} className="block py-1.5 text-sm text-navy/70">{c.label}</Link>
                        )
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <CTAButton to="/register" className="mt-3 w-full">Register Now</CTAButton>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
